import { Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { useTranslation } from 'react-i18next';
import config from "../../../lib/config";
import exportTitleTable from "../../../lib/exportTitleTable";
import { IExamStatsByClass } from "../../../models/statistics.model";
import DownloadButton from "../../DownloadButton";
import Pagination from "../../Pagination";

interface IProps {
  rawData: IExamStatsByClass[]|undefined;
  excelParam: {excelCampusCodes: string[]}|undefined;
}

const ExamStatsByClass: FC<IProps> = ({
  rawData,
  excelParam
}) => {
  const { t } = useTranslation();
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const [data, setData] = useState<IExamStatsByClass[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (rawData) {
      let _data: any = _.cloneDeep(rawData);
      if (rawData?.length) {
        _data.forEach((item: any, idx: number) => {
          item.subjectInfo?.forEach((subitem: any) => {
            item[subitem.subjectName] = subitem.subjectScore;
          });
          item.rowIdx = idx;
        });
      }

      setData(_data);
    }
  }, [rawData]);

  const getColumns = () => {
    const firstColumns: TableColumnsType<IExamStatsByClass> = [
      {
        title: t('common.menu.campus'),
        dataIndex: 'campusKorName',
        key: 'campusKorName',
        align: 'center',
      },
      {
        title: t('common.menu.class'),
        dataIndex: 'className',
        key: 'className',
        align: 'center',
      },
      {
        title: t('class.class.column3'),
        dataIndex: 'examName',
        key: 'examName',
        align: 'center',
      },
      {
        title: t('student.student.column29'),
        dataIndex: 'teacherKorName',
        key: 'teacherKorName',
        align: 'center',
      },
      {
        title: 'Overall',
        dataIndex: 'overall',
        key: 'overall',
        align: 'center',
        render: value => Math.round(value),
      },
    ];
    const lastColumns: TableColumnsType<IExamStatsByClass> = [
      {
        title: t('statistics.placementTest.column1'),
        dataIndex: 'averageTimeRequired',
        key: 'averageTimeRequired',
        align: 'center',
        render: value => Math.round(value),
      },
      {
        title: t('statistics.regularTest.column1'),
        dataIndex: 'totalRequireCount',
        key: 'totalRequireCount',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column3'),
        dataIndex: 'participantCount',
        key: 'participantCount',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column4'),
        dataIndex: 'nonParticipantCount',
        key: 'nonParticipantCount',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column5'),
        dataIndex: 'participantRate',
        key: 'participantRate',
        align: 'center',
        render: value => Math.round(value),
      },
    ];
    const middleColumns: TableColumnsType<IExamStatsByClass> = [];

    if (rawData?.length) {
      //과목 정렬 처리
      let subjectOrderMap : Map<number, any> = new Map<number, any>();

      // @ts-ignore
      rawData.at(0).subjectInfo?.forEach(item => {
        if(item.subjectName === "Listening") {
          subjectOrderMap.set(1, item.subjectName);
        } else if(item.subjectName === "Reading") {
          subjectOrderMap.set(2, item.subjectName);
        } else if(item.subjectName === "Vocabulary") {
          subjectOrderMap.set(3, item.subjectName);
        } else if(item.subjectName === "Grammar") {
          subjectOrderMap.set(4, item.subjectName);
        } else {
          subjectOrderMap.set(5, item.subjectName);
        }
      });

      const sortedSubjectInfo = new Map([...subjectOrderMap.entries()].sort((a,b) => a[0] - b[0]));

      sortedSubjectInfo.forEach(item => {
        middleColumns.push({
          title: item,
          dataIndex: item,
          key: item,
          align: 'center',
          render: value => Math.round(value),          
        });
      });
    }

    return _.concat(firstColumns, middleColumns, lastColumns);
  }
  
  const onExportTable = () => {
    const currentDate = moment(new Date());

    let excelTitle = `${t('statistics.regularTest.title1')}(${t('common.menu.class')})`;
    let arrSubTitle = [];
    let campusNames = '';

    if(excelParam?.excelCampusCodes) {
      if(excelParam.excelCampusCodes.length === 0) {
        campusNames = t('common.text.all');
      } else {
        excelParam.excelCampusCodes.forEach((item) => {
          if(campusNames !== '') {
            campusNames += ', '  
          }
          campusNames += campusData.find(campus => item === campus.campusCode)?.campusKorName;
        });
      }
    }

    arrSubTitle.push(t('common.excel.campus') + " : " + campusNames);

    exportTitleTable(getColumns(), data, `${t('statistics.regularTest.title1')}(${t('common.menu.class')})_(${currentDate.format('YYYYMMDD')})`, `${t('common.menu.class')}`, [12, 20, 30, 10, 15, 15, 15, 15, 12, 10, 10, 10, 12], excelTitle, arrSubTitle);
  }
  
  return (
    <>
      <div className="comm-table__area">
        <Table
          columns={getColumns()}
          dataSource={data}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
          rowKey={'rowIdx'}
        />
      </div>
      <div className="comm-bx__between">
        <DownloadButton onClick={onExportTable} />
        <div className="common-paging center">
          <Pagination
            count={data.length}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>
    </>
  );
}

export default ExamStatsByClass;
